import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import startCase from 'lodash/startCase';
import { Swiper, SwiperSlide } from 'swiper/react';
import useWindowSize from '@lib/window-size';
import { pushDataLayerEvent } from '@lib/gtm';

// ---------------------------------------------------------

import PropertyImagesModal from '@components/property-images-modal/component';
import Button from '@components/button';
import Icon from '@components/icon';
import Image from '@components/image';
import LikeButton from '@components/like-button';

// ---------------------------------------------------------

import { Keyboard, Navigation, Thumbs } from 'swiper/modules';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  gallery_container,
  only_one_image,
  img1,
  img2,
  img3,
  img3_overlay,
  mobile_image_pills,
  property_status,
  property_status_sold,
  virtual_tour,
  is_mobile,
  is_desktop
} = styles;
import 'swiper/css/keyboard';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import 'swiper/css';

// ---------------------------------------------------------

const PropertyImages = ({
  virtualTourUrl,
  property,
  canonicalUrl,
  sharingServicesCollection
}) => {
  const router = useRouter();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const windowSize = useWindowSize();

  const isDesktop = (windowSize.width || 0) > 720;
  const mobileWidth = windowSize.width || 500; // Fallback to 500px if undefined

  useEffect(() => {
    if (router?.isReady) {
      setModalIsOpen(!!router?.query?.modal);
    }
  }, [router]);

  const handleOpenModal = () => {
    const currentUrl = router.asPath;
    const urlWithoutQuery = currentUrl.split('?')[0];
    const newQuery = { agentid: router.query.agentid };

    if (!modalIsOpen && router.query.agentid) {
      pushDataLayerEvent('listing_image_modal_click', router.asPath);
      return router.replace(
        { pathname: urlWithoutQuery, query: { ...newQuery, modal: true } },
        undefined,
        {
          shallow: true
        }
      );
    }

    if (!modalIsOpen && !router.query.agentid) {
      pushDataLayerEvent('listing_image_modal_click', router.asPath);
      return router.replace({ pathname: urlWithoutQuery, query: { modal: true } }, undefined, {
        shallow: true
      });
    }
  };
  const handleVirtualTourClick = (e) => {
    e.stopPropagation();
    pushDataLayerEvent('listing_virtual_tour_click', router.asPath);
    window.open(virtualTourUrl, '_blank');
  };

  const desktopClasses = classNames(gallery_container, is_desktop, {
    [only_one_image]: property.images.length === 1
  });
  const mobileClasses = classNames(gallery_container, is_mobile, {
    [only_one_image]: property.images.length === 1
  });

  // ---------------------------------------------------------

  return (
    property.images && (
      <>
        {isDesktop && (
          <div className={desktopClasses}>
            {property.images[0] && (
              <div className={img1}>
                <LikeButton id={property.listingId} />
                <span
                  role="button"
                  tabIndex="0"
                  onKeyDown={() => handleOpenModal()}
                  onClick={() => handleOpenModal()}
                >
                  <Image
                    key={property.listingId + '0-dsk'}
                    priority={true}
                    layout="fill"
                    objectFit="cover"
                    backgroundImage
                    objectPosition="center"
                    image={{
                      height: 590,
                      src: property.images[0].url,
                      width: 735
                    }}
                  />
                </span>
                {property.alertPills && (
                  <ul className={property_status}>
                    {property.alertPills.map((stat, index) => {
                      return (
                        <li key={`property-alert-${property.listingId}-${index}`}>
                          <span
                            className={startCase(stat) === 'Sold' ? property_status_sold : null}
                          >
                            {startCase(stat)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {property.images[1] && (
              <div className={img2}>
                <span
                  role="button"
                  tabIndex="0"
                  onKeyDown={() => handleOpenModal()}
                  onClick={() => handleOpenModal()}
                >
                  <Image
                    key={property.listingId + '1-dsk'}
                    priority={true}
                    layout="fill"
                    objectFit="cover"
                    objectPosition="center"
                    backgroundImage
                    image={{
                      height: 266,
                      src: property.images[1].url,
                      width: 316
                    }}
                  />
                </span>
              </div>
            )}

            {property.images[2] && (
              <div className={img3}>
                <>
                  <span
                    role="button"
                    tabIndex="0"
                    onKeyDown={() => handleOpenModal()}
                    onClick={() => handleOpenModal()}
                  >
                    <Image
                      key={property.listingId + '2dsk'}
                      priority={true}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      backgroundImage
                      image={{
                        height: 315,
                        src: property.images[2].url,
                        width: 316
                      }}
                    />
                    <div className={img3_overlay}>
                      <Button
                        icon="photos"
                        label={`View ${property.images.length} Photos`}
                        type="button"
                        theme="with_icon"
                      />
                      {virtualTourUrl && (
                        <div className={virtual_tour}>
                          <Button
                            label="Virtual Tour"
                            icon="video"
                            type="button"
                            theme="with_icon"
                            onClick={handleVirtualTourClick}
                            target="_blank"
                          />
                        </div>
                      )}
                    </div>
                  </span>
                </>
              </div>
            )}
          </div>
        )}

        {!isDesktop && (
          <div
            className={mobileClasses}
            style={{ height: mobileWidth * 0.67, maxWidth: mobileWidth }}
          >
            <Swiper modules={[Keyboard, Navigation, Thumbs]} slidesPerView={'auto'}>
              {property.images.slice(0, 3).map((image, index) => (
                <SwiperSlide key={`swiper-${index}`} style={{ flexShrink: '1' }}>
                  <div className={img1}>
                    {index === 0 && <LikeButton id={property.listingId} />}
                    <span
                      role="button"
                      tabIndex="0"
                      onKeyDown={handleOpenModal}
                      onClick={handleOpenModal}
                      style={index === 2 ? { filter: 'brightness(0.6)' } : {}}
                    >
                      <Image
                        image={{
                          height: mobileWidth * 0.67 || 335,
                          src: image.url,
                          width: mobileWidth || 500
                        }}
                        layout="fill"
                        objectFit="cover"
                      />
                      <div className={mobile_image_pills}>
                        <span>
                          <Icon type="filters" name="photos" />
                          {index + 1}/{property.images.length}
                        </span>
                      </div>
                    </span>

                    {index === 0 && property.alertPills && (
                      <ul className={property_status}>
                        {property.alertPills.map((stat, index) => {
                          return (
                            <li key={`swipe-property-alert-${property.listingId}-${index}`}>
                              <span
                                className={startCase(stat) === 'Sold' ? property_status_sold : null}
                              >
                                {startCase(stat)}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {index === 2 && (
                      <div className={img3_overlay}>
                        <Button
                          icon="photos"
                          label={`View ${property.images.length} photos`}
                          type="button"
                          theme="with_icon"
                          onClick={() => handleOpenModal()}
                        />
                      </div>
                    )}
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        )}

        <PropertyImagesModal
          canonicalUrl={canonicalUrl}
          sharingServicesCollection={sharingServicesCollection}
          setModalIsOpen={setModalIsOpen}
          modalIsOpen={modalIsOpen}
          property={property}
        />
      </>
    )
  );
};

PropertyImages.propTypes = {
  /**
   * Canonical Url to be displayed and copied onto the clipboard
   */
  canonicalUrl: PropTypes.string,

  /**
   * Specifies the details of a property
   */
  property: PropTypes.shape({
    address: PropTypes.string,
    alertPills: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    images: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        url: PropTypes.string
      })
    ),
    listingId: PropTypes.number,
    listingUrl: PropTypes.string
  }),

  /**
   * Collection of share items to be displayed within the modal
   */
  sharingServicesCollection: PropTypes.arrayOf(
    PropTypes.shape({
      shareName: PropTypes.string,
      shareUrl: PropTypes.string
    })
  ),

  /**
   * Specifies the virtualTour URL which opens a new page
   */
  virtualTourUrl: PropTypes.string
};

export default PropertyImages;
